import AsyncLock from "AsyncLock";
import type { DependencyResult } from "DependencyResult";
import { getInterfaceName } from "EntityExtensions";
import { trackEntityManagerOperationAsync } from "EntityManagerExtensions";
import {
  findError,
  isNetworkError,
  isTransientError,
  matchError,
  PropertyRestrictionError,
  UnavailableArgumentsOrSecurityError,
} from "Errors";
import { withPausing, type PausableObservable } from "KnockoutExtensions";
import type Rule from "Rule";
import { State } from "StateConstants";
import type { RuleProcessResult } from "SubRule";
import validationEngine from "ValidationEngine";
import type { Entity } from "breeze-client";
import ko from "knockout";
import { noop } from "lodash-es";
import DependencyGraphStrategy from "./DependencyGraphStrategy.ts";
import type { DependencyStrategyContext } from "./DependencyStrategyContext.ts";

/*! StartNoStringValidationRegion property name */
export interface CalculatedProperty<T>
  extends Omit<CalculatedPropertyPrototype<T>, "subscribe">,
    Pick<ko.Computed<T>, "subscribe"> {
  (): T; // TODO: Change the interface so that the value is nullable, then update usages.
  (value: T): void;
}
/*! EndNoStringValidationRegion */

interface CalculatedPropertyInternals<T> {
  _context: CalculatedPropertyContext<T>;
}

interface Loader<T> {
  promise: Promise<LoadResult<T>>;
  shouldRefresh: boolean;
}

interface LoadResult<T> {
  notificationPromise?: Promise<void>;
  value: T | null;
}

interface SetterState {
  isDisposed: boolean;
  next?: SetterState;
  shouldRefresh: boolean;
}

interface SubscriptionInternals extends ko.Subscription {
  _target?: unknown;
}

/*! SuppressStringValidation type */
const defaultSubscriptionEvent = "change";

class CalculatedPropertyPrototype<T> extends Function {
  private readonly _context: CalculatedPropertyContext<T>;

  // This contructor is not actually used. It exists to satisfy TypeScript's requirement that _context is initialized.
  constructor(context: CalculatedPropertyContext<T>) {
    super();
    this._context = context;
  }

  get isCalculatedProperty(): boolean {
    return true;
  }

  get hasWriteFunction(): boolean {
    return this._context.rule.hasSetter();
  }

  disableSubscriptionsOnNextNotification(): void {
    const context = this._context;
    enableOneTimeNotificationForObservable(context.observable);
    enableOneTimeNotificationForObservable(context.state);
  }

  dispose(): void {
    const context = this._context;
    context.dependencyStrategy.dispose();
    context.isDisposed = true;
  }

  // Perhaps instead of this, we should store the state on the EntityManager.
  // Then have the ability to import state from a different EntityManager in order to import calculated property values.
  async forceWriteAsync(value: T): Promise<void> {
    const context = this._context;

    const promise = ko.ignoreDependencies(async () => {
      if (context.rule.hasSetter()) {
        return await this.writeAsync(value);
      } else if (context.state() !== State.Available || context.observable() !== value) {
        const initialLoader = context.dependencyStrategy.performInitialLoad();
        if (initialLoader) {
          await initialLoader;
        }

        setValue(context, State.Available, value);
        await context.dependencyStrategy.notifyDependentsAsync(false);
        await validateAsync(context);
      }
    });

    await addOperationPromiseAsync(context.entity, promise);
  }

  getRuleDefinition(): Rule {
    return this._context.rule;
  }

  getState(): State {
    return this._context.state.peek();
  }

  async loadAsync(): Promise<T> {
    const { value } = await loadAsync(this._context);
    return value as T; // TODO: Change the interface so that the value is nullable, then update usages.
  }

  observeState(): State {
    return this._context.state();
  }

  peek(): T {
    const context = this._context;
    load(context);
    return context.observable.peek() as T; // TODO: Change the interface so that the value is nullable, then update usages.
  }

  async refreshBindingAsync(): Promise<void> {
    this.resetState();
    await this.loadAsync();
  }

  resetState(): void {
    const context = this._context;
    if (context.state.peek() === State.Available) {
      context.state(State.NotLoaded);
      context.observable(null);
      context.error = undefined;
      context.initiallyLoaded = false;

      context.dependencyStrategy.notifyDependentsAsync(true);
    }
  }

  subscribe(
    callback: ko.SubscriptionCallback<unknown, unknown>,
    callbackTarget?: unknown,
    event = defaultSubscriptionEvent,
  ): ko.Subscription {
    return this._context.observable.subscribe(callback, callbackTarget, event);
  }

  valueHasMutated(): void {
    this._context.observable.valueHasMutated();
  }

  async writeAsync(value: T): Promise<void> {
    const context = this._context;
    const { entity, rule } = context;

    const promise = ko.ignoreDependencies(async () => {
      ensureSettable(context);

      if (context.state() === State.Available && context.observable() === value) {
        return;
      }

      setValue(context, State.Available, value);
      const setterState = createSetterState(context);

      return await context.setterLock.doAsync(async () => {
        try {
          const initialLoader = context.dependencyStrategy.performInitialLoad();
          if (initialLoader) {
            await initialLoader;
          }
          if (setterState.isDisposed) {
            return;
          }
          context.setterState = setterState;

          let result;
          try {
            result = await rule.invokeSetterAsync(entity, value);
            if (setterState.isDisposed) {
              return;
            }
            if (!result) {
              setValue(context, State.NotAvailable, null);
            }

            await context.dependencyStrategy.notifyDependentsAsync(false);
          } catch (error) {
            if (setterState.isDisposed) {
              return;
            }
            if (error instanceof Error && findError(error, UnavailableArgumentsOrSecurityError)) {
              setValue(context, State.NotAvailable, null);
            } else {
              throw error;
            }
          }

          if (result && !setterState.isDisposed) {
            await validateAsync(context);
          }
        } finally {
          if (!setterState.isDisposed) {
            context.setterState = undefined;
            if (setterState.shouldRefresh) {
              handleDependenciesChangedAsync(this._context);
            }
          }
        }
      });
    });

    await addOperationPromiseAsync(entity, promise);
  }
}

const { prototype } = CalculatedPropertyPrototype;
Object.assign(prototype, { __ko_proto__: ko.computed });

function enableOneTimeNotificationForObservable<T>(observable: ko.Observable<T>): void {
  const subscriptions: Record<string, SubscriptionInternals[]> = {};

  const subscribe = observable.subscribe;
  observable.subscribe = function (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: ko.SubscriptionCallback<any, unknown>,
    callbackTarget?: unknown,
    event?: string,
  ): ko.Subscription {
    event = event || defaultSubscriptionEvent;
    const subscription: SubscriptionInternals = subscribe.call(this, callback, callbackTarget, event);
    if (!subscriptions[event]) {
      subscriptions[event] = [];
    }
    subscriptions[event].push(subscription);
    return subscription;
  };

  const notifySubscribers = observable.notifySubscribers;
  observable.notifySubscribers = function (value?: T, event?: string): void {
    event = event || defaultSubscriptionEvent;
    notifySubscribers.call(this, value, event);
    observable.subscribe = dummySubscribe;
    const subscriptionsOfThisType = subscriptions[event];
    if (subscriptionsOfThisType) {
      const dummySubscription = dummySubscribe();
      subscriptionsOfThisType.forEach((s) => {
        s.dispose();
        s._target = dummySubscription._target;
      });
      delete subscriptions[event];
    }
  };
}

function dummySubscribe(): SubscriptionInternals {
  const result = {
    dispose: noop,
    _target: {
      _notificationIsPending: false,
      hasChanged: noop,
      _deferUpdates: false,
      subscribe: noop,
    },
    _isDisposed: true,
    disposeWhenNodeIsRemoved: noop,
  };

  return result;
}

function load<T>(context: CalculatedPropertyContext<T>): void {
  if (!context.isDisposed && !context.loader && !context.error) {
    loadCoreAsync(context);
  }
}

async function loadAsync<T>(context: CalculatedPropertyContext<T>): Promise<LoadResult<T>> {
  if (context.isDisposed) {
    return { value: context.observable.peek() };
  }
  if (context.loader) {
    return await context.loader.promise;
  }
  if (context.error) {
    throw context.error;
  }

  return await loadCoreAsync(context);
}

const failedRuleProcessResult: RuleProcessResult = Object.freeze({ isSuccess: false, value: undefined });

async function loadCoreAsync<T>(context: CalculatedPropertyContext<T>): Promise<LoadResult<T>> {
  return await ko.ignoreDependencies(async () => {
    const { rule } = context;
    const isNotLoaded = context.state() === State.NotLoaded;
    if (!isNotLoaded && !rule.isCacheDisabled()) {
      return { value: context.observable() };
    }

    const initialLoad = context.dependencyStrategy.performInitialLoad();
    if (rule.isCacheDisabled()) {
      const result = tryProcessRuleSync(context);
      if (result.state !== State.NotLoaded) {
        const value = result.state === State.Available ? (result.value as T | null) : null;
        setValue(context, result.state, value, undefined, !isNotLoaded);
        return { value };
      }
    }

    // Using type assertion here to simplify the initial refactor. Possibly this can be improved later as in theory,
    // the promise can execute code that touches loader.promise before we assign the value.
    const loader = (context.loader = { promise: undefined as unknown as Promise<LoadResult<T>>, shouldRefresh: false });
    loader.promise = (async (): Promise<LoadResult<T>> => {
      let result = failedRuleProcessResult;
      try {
        if (initialLoad) {
          await initialLoad;
        }

        do {
          loader.shouldRefresh = false;
          try {
            result = await context.rule.processAsync(context.entity);
          } catch (error) {
            if (!loader.shouldRefresh) {
              throw error;
            }
          }
        } while (loader.shouldRefresh);
      } catch (error) {
        if (loader === context.loader) {
          if (!(error instanceof Error)) {
            throw error; // This should never happen so we won't handle it.
          }
          if (
            !matchError(
              error,
              (e) => e instanceof UnavailableArgumentsOrSecurityError || e instanceof PropertyRestrictionError,
            )
          ) {
            if (matchError(error, (e) => isNetworkError(e) || isTransientError(e))) {
              context.loader = undefined;
            } else {
              setValue(context, State.NotAvailable, context.observable(), error);
            }
            throw error;
          }
        }
      }

      if (loader === context.loader) {
        const state = result.isSuccess ? State.Available : State.NotAvailable;
        const value = result.isSuccess ? (result.value as T | null) : null;
        const oldValue = context.observable();
        const { initiallyLoaded } = context;
        context.initiallyLoaded = true;
        setValue(context, state, value);
        if (oldValue === value) {
          context.observable.valueHasMutated();
        }

        return {
          value,
          notificationPromise: context.dependencyStrategy.notifyDependentsAsync(!initiallyLoaded),
        };
      }

      return await loadAsync(context);
    })();

    return await loader.promise;
  });
}

function tryProcessRuleSync<T>(context: CalculatedPropertyContext<T>): DependencyResult {
  try {
    return context.rule.tryProcessSync(context.entity);
  } catch (error) {
    if (error instanceof Error && findError(error, UnavailableArgumentsOrSecurityError)) {
      return { state: State.NotAvailable };
    }
    throw error;
  }
}

function createSetterState<T>(context: CalculatedPropertyContext<T>): SetterState {
  const { setterState: current } = context;
  const next: SetterState = { isDisposed: false, next: undefined, shouldRefresh: false };
  if (current) {
    if (current.next) {
      current.next.isDisposed = true;
    }
    current.isDisposed = true;
    current.next = next;
  } else {
    context.setterState = next;
  }

  return next;
}

function ensureSettable<T>(
  context: CalculatedPropertyContext<T>,
): asserts context is CalculatedPropertyContext<T> & { setterLock: AsyncLock } {
  if (!context.setterLock) {
    throw new Error(
      `Cannot write to calculated property ${getInterfaceName(context.entity)}.${context.rule.property} because it does not have a setter.`,
    );
  }
}

async function addOperationPromiseAsync<T>(entity: Entity, promise: Promise<T>): Promise<T> {
  const { entityManager } = entity.entityAspect;
  if (entityManager) {
    promise = trackEntityManagerOperationAsync(entityManager, promise);
  }
  return await promise;
}

function setValue<T>(
  context: CalculatedPropertyContext<T>,
  state: State,
  value: T | null,
  error?: Error,
  poke?: boolean,
): void {
  context.state(state);
  context.error = error;
  context.loader = undefined;

  const { observable } = context;
  if (observable() !== value) {
    if (poke) {
      if (!("poke" in observable)) {
        throw new Error("Cannot poke because observable is not pausable.");
      }
      observable.poke(value);
    } else {
      observable(value);
    }
    context.dependencyStrategy.handleValueChanged(value);
  }
}

function calculatedProperty<T>(entity: Entity, rule: Rule): CalculatedProperty<T> {
  const instance = function (value?: T): T | null | undefined {
    const context = instance._context;
    if (arguments.length === 0) {
      load(context);
      return context.observable();
    } else {
      ensureSettable(context);
      instance.writeAsync(value as T);
      return;
    }
  } as unknown as CalculatedProperty<T> & CalculatedPropertyInternals<T>;

  Object.setPrototypeOf(instance, prototype);
  instance._context = new CalculatedPropertyContext(entity, rule);

  return instance;
}

async function handleDependenciesChangedAsync<T>(context: CalculatedPropertyContext<T>): Promise<void> {
  if (context.setterState) {
    context.setterState.shouldRefresh = true;
    return;
  }

  context.state(State.NotLoaded);
  context.error = undefined;

  let { loader } = context;
  if (loader) {
    loader.shouldRefresh = true;
  } else {
    load(context);
    ({ loader } = context);
    if (loader) {
      const { notificationPromise } = await loader.promise;
      if (notificationPromise) {
        await notificationPromise;
      }
    }
  }
}

async function validateAsync<T>(context: CalculatedPropertyContext<T>): Promise<void> {
  const { property } = context.rule;
  if (!property) {
    return;
  }

  const { entity } = context;
  const { entityState } = entity.entityAspect;
  if (entityState.isDeleted() || entityState.isDetached()) {
    return;
  }

  await validationEngine.validatePropertyAndDependentsAsync(entity, property);
}

class CalculatedPropertyContext<T> implements DependencyStrategyContext {
  dependencyStrategy: DependencyGraphStrategy;
  error?: Error;
  initiallyLoaded: boolean;
  isDisposed: boolean;
  loader?: Loader<T>;
  readonly observable: ko.Observable<T | null> | PausableObservable<T | null>;
  readonly setterLock?: AsyncLock;
  setterState?: SetterState;
  readonly state: ko.Observable<State>;

  constructor(
    readonly entity: Entity,
    readonly rule: Rule,
  ) {
    let observable = ko.observable(null);
    if (rule.isCacheDisabled()) {
      observable = withPausing(observable);
    }

    this.dependencyStrategy = new DependencyGraphStrategy(this);
    this.error = undefined;
    this.initiallyLoaded = false;
    this.isDisposed = false;
    this.loader = undefined;
    this.observable = observable;
    this.setterLock = rule.hasSetter() ? new AsyncLock() : undefined;
    this.setterState = undefined;
    this.state = ko.observable<State>(State.NotLoaded);
  }

  getState(): State {
    return this.state.peek();
  }

  async handleChangedAsync(): Promise<void> {
    return await handleDependenciesChangedAsync(this);
  }
}

export default calculatedProperty;
